import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Alert, Button, Form, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { useDebounce } from 'use-debounce';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_SM,
  SUCCESS_BG,
  SUCCESS_TEXT,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';
import { fmtInt, fmtUsd } from 'components/upb_match_explorer/format';
import FsaLoader from 'components/shared/FsaLoader';
import {
  getURLWithSearchParams,
  MANUFACTURER_SKU_MAPPING_MANUFACTURERS,
} from 'lib/networking/endpoints';
import { httpGet } from 'lib/networking/http';
import InfluenceMapProductsModal from 'components/influence_sku_mapping/InfluenceMapProductsModal';

const PAGE_SIZE = 20;
const MAPPED_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'mapped', label: 'Mapped' },
  { key: 'unmapped', label: 'Unmapped' },
];

/**
 * InfluenceManufacturerProductsView — the dashboard's "Analyze" drill-in: a themed,
 * paginated list of a manufacturer's branded distributor products, each with a
 * thumbnail and whether it is mapped to the manufacturer's catalog.
 *
 * ``embedded`` drops the back link / title / subtitle and page chrome so it can render inside the
 * drill-in's Mapped/Unmapped tabs; ``fixedMapped`` ('mapped'|'unmapped') locks the mapped filter
 * and hides the All/Mapped/Unmapped segment.
 */
function InfluenceManufacturerProductsView({ embedded, fixedMapped }) {
  const history = useHistory();
  const { manufacturerId, distributorId } = useParams();
  const { search } = useLocation();
  // Inherited from the dashboard/distributors filter: keep the product list on the same
  // campaign-product-status brand set as the views that navigated here.
  const campaignStatus = new URLSearchParams(search).get('campaign_product_status') || '';

  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [mappedFilter, setMappedFilter] = useState(fixedMapped || 'all');
  // Debounce the search so typing fires one request after it settles, not per keystroke.
  const [debouncedQuery] = useDebounce(query.trim(), 300);

  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Map Products modal: index of the row it opened on (null = closed). Mapping is only offered
  // when the list is scoped to unmapped products, so the modal's navigator is all actionable rows.
  const [mapIndex, setMapIndex] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const mappedDirty = useRef(false);
  // Monotonic request token: only the latest in-flight request may write state,
  // so an out-of-order (stale) response never overwrites a newer one.
  const requestSeq = useRef(0);

  // New search or filter always restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, mappedFilter]);

  useEffect(() => {
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (debouncedQuery) {
      params.query = debouncedQuery;
    }
    if (mappedFilter !== 'all') {
      params.mapped = mappedFilter === 'mapped';
    }
    if (distributorId) {
      params.distributor_id = distributorId;
    }
    if (campaignStatus) {
      params.campaign_product_status = campaignStatus;
    }
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    setError(false);
    httpGet(
      getURLWithSearchParams(
        `${MANUFACTURER_SKU_MAPPING_MANUFACTURERS}/${manufacturerId}/products`,
        params,
      ),
    )
      .then(res => {
        if (seq !== requestSeq.current) {
          return; // a newer request superseded this one — ignore the stale result
        }
        setData(res.data);
      })
      .catch(() => {
        if (seq === requestSeq.current) {
          setError(true);
        }
      })
      .finally(() => {
        if (seq === requestSeq.current) {
          setLoading(false);
        }
      });
  }, [
    manufacturerId,
    distributorId,
    campaignStatus,
    page,
    debouncedQuery,
    mappedFilter,
    refreshKey,
  ]);

  const products = data?.products || [];
  const count = data?.count || 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  // A post-map refresh can shrink the list; clamp a now-out-of-range page so it isn't empty.
  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);
  // When scoped to a distributor, its name comes off the product rows (all share it).
  const distributorName = distributorId ? products[0]?.distributor_name : null;

  // Mapping is offered only when the list is scoped to unmapped products (the Unmapped tab, or the
  // Unmapped segment filter) so every row in the modal's navigator is actionable.
  const canMap = fixedMapped === 'unmapped' || mappedFilter === 'unmapped';
  const closeMapModal = () => {
    setMapIndex(null);
    if (mappedDirty.current) {
      mappedDirty.current = false;
      setRefreshKey(k => k + 1); // a map happened -> refresh the list + count
    }
  };

  const setFilter = key => setMappedFilter(key);
  const onSearch = value => setQuery(value);
  const openProduct = product =>
    window.open(
      `/global-product-detail/${product.global_product_id}/${product.id}/distributor`,
      '_blank',
      'noopener,noreferrer',
    );

  // Initial load (no products yet): one full-view loader. Re-fetches on filter/search keep the
  // controls and use the in-place loader below, so typing never blanks the whole page.
  if (loading && !data) {
    return (
      <div className={css(styles.loadingWrap)}>
        <FsaLoader fullView={!embedded} size={embedded ? 64 : 96} label="Loading products…" />
      </div>
    );
  }

  return (
    <div className={css(styles.page, embedded && styles.embeddedPage)}>
      {!embedded && (
        <Button
          variant="link"
          type="button"
          className={css(styles.back)}
          onClick={() =>
            history.push(
              distributorId
                ? `/influence-sku-mapping/manufacturers/${manufacturerId}`
                : '/influence-sku-mapping',
            )
          }
        >
          {distributorId ? '← Distributors' : '← Influence SKU mapping'}
        </Button>
      )}
      {!embedded && (
        <h1 className={css(styles.title)}>{data?.manufacturer_name || 'Manufacturer'}</h1>
      )}
      {!embedded && (
        <p className={css(styles.subtitle)}>
          {distributorName && <span className={css(styles.subtitleDp)}>{distributorName}</span>}
          {distributorName ? ' — ' : ''}
          Branded products on influence-enabled distributors.
        </p>
      )}

      <div className={css(styles.controls)}>
        {!fixedMapped && (
          <div className={css(styles.segment)}>
            {MAPPED_FILTERS.map(f => (
              <Button
                key={f.key}
                variant="link"
                type="button"
                className={css(
                  styles.segmentBtn,
                  mappedFilter === f.key && styles.segmentBtnActive,
                )}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        )}
        <Form.Control
          className={css(styles.search)}
          type="search"
          placeholder="Search name or SKU"
          value={query}
          onChange={e => onSearch(e.target.value)}
        />
      </div>

      {error ? (
        <Alert variant="danger">Failed to load products. Please try again.</Alert>
      ) : loading ? (
        <div className={css(styles.loading)}>
          <FsaLoader label="Loading products…" />
        </div>
      ) : (
        <>
          <div className={css(styles.tableMeta)}>
            {fmtInt(count)} {count === 1 ? 'product' : 'products'} matching the current filters
          </div>
          <div className={css(styles.tableWrap)}>
            <Table hover className={css(styles.table)}>
              <thead>
                <tr>
                  <th className={css(styles.thumbCol)} />
                  <th>Product</th>
                  <th>Brand</th>
                  <th>Distributor</th>
                  <th>Pack</th>
                  <th>Status</th>
                  <th>Mapped</th>
                  <th>Mfr SKU</th>
                  <th className={css(styles.gmvCell)}>GMV</th>
                  <th className={css(styles.actionCol)} />
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td colSpan="10" className={css(styles.empty)}>
                      No branded products match the current filters.
                    </td>
                  </tr>
                ) : (
                  products.map((product, i) => (
                    <tr key={product.id}>
                      <td className={css(styles.thumbCol)}>
                        {product.thumbnail_url ? (
                          <img
                            className={css(styles.thumb)}
                            src={product.thumbnail_url}
                            alt=""
                            onError={e => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className={css(styles.thumbFallback)}>
                            {(product.brand_name || product.name || '?').charAt(0)}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className={css(styles.prodName)}>{product.name || '—'}</div>
                        <div className={css(styles.prodSub)}>SKU {product.vendor_sku || '—'}</div>
                      </td>
                      <td>{product.brand_name || '—'}</td>
                      <td>{product.distributor_name || '—'}</td>
                      <td>{product.pack_size || '—'}</td>
                      <td>{product.active_status || '—'}</td>
                      <td>
                        {product.mapped ? (
                          <span className={css(styles.badge, styles.badgeMapped)}>Mapped</span>
                        ) : (
                          <span className={css(styles.badge, styles.badgeUnmapped)}>Unmapped</span>
                        )}
                      </td>
                      <td className={css(styles.mfrSkuCell)}>{product.mfr_sku || '—'}</td>
                      <td className={css(styles.gmvCell)}>
                        {product.gmv != null ? fmtUsd(product.gmv) : '—'}
                      </td>
                      <td className={css(styles.actionCol)}>
                        <div className={css(styles.rowActions)}>
                          {canMap && !product.mapped && (
                            <Button variant="link" size="sm" onClick={() => setMapIndex(i)}>
                              Map →
                            </Button>
                          )}
                          <Button variant="link" size="sm" onClick={() => openProduct(product)}>
                            View →
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className={css(styles.pager)}>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              ← Prev
            </Button>
            <span className={css(styles.pageInfo)}>
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next →
            </Button>
          </div>
        </>
      )}

      {canMap && (
        <InfluenceMapProductsModal
          show={mapIndex !== null}
          onHide={closeMapModal}
          manufacturerId={manufacturerId}
          products={products}
          startIndex={mapIndex || 0}
          onMapped={() => {
            mappedDirty.current = true;
          }}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: { background: BG_2, minHeight: '100%', padding: '28px 32px 48px' },
  // Rendered inside the drill-in's Mapped/Unmapped tab: no own page chrome.
  embeddedPage: { background: 'transparent', minHeight: 0, padding: 0 },
  loadingWrap: { background: BG_2 },
  back: {
    display: 'inline-block',
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 13,
    fontWeight: 600,
    color: PRIMARY_COLOR,
    cursor: 'pointer',
    marginBottom: 12,
    textDecoration: 'none',
    ':hover': { color: PRIMARY_COLOR, textDecoration: 'none' },
    ':focus': { textDecoration: 'none', boxShadow: 'none' },
  },
  title: { fontSize: 24, fontWeight: 700, color: FG_1, margin: '0 0 4px' },
  subtitle: { fontSize: 14, color: FG_2, marginBottom: 20 },
  subtitleDp: { color: PRIMARY_COLOR, fontWeight: 700 },
  tableMeta: { fontSize: 13, color: FG_3, marginBottom: 10, fontWeight: 600 },
  mfrSkuCell: { fontVariantNumeric: 'tabular-nums', color: FG_2 },
  gmvCell: {
    fontVariantNumeric: 'tabular-nums',
    color: FG_2,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 14,
  },
  segment: {
    display: 'inline-flex',
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    overflow: 'hidden',
    background: BG_1,
  },
  segmentBtn: {
    background: 'none',
    border: 'none',
    borderRadius: 0,
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: FG_2,
    cursor: 'pointer',
    textDecoration: 'none',
    ':hover': { color: FG_1, textDecoration: 'none' },
    ':focus': { textDecoration: 'none', boxShadow: 'none' },
  },
  segmentBtnActive: {
    background: PRIMARY_COLOR,
    color: WHITE,
    ':hover': { color: WHITE, textDecoration: 'none' },
  },
  search: {
    maxWidth: 260,
    fontSize: 13,
    borderRadius: RADIUS_LG,
    border: `1px solid ${BORDER_2}`,
    background: BG_1,
  },
  loading: { display: 'flex', justifyContent: 'center', padding: 40, color: PRIMARY_COLOR },
  tableWrap: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    overflowX: 'auto',
  },
  table: { fontSize: 13, color: FG_2, marginBottom: 0, verticalAlign: 'middle' },
  thumbCol: { width: 56 },
  actionCol: { textAlign: 'right', whiteSpace: 'nowrap' },
  rowActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' },
  thumb: { width: 40, height: 40, borderRadius: 6, objectFit: 'contain', background: BG_1 },
  thumbFallback: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 6,
    background: BORDER_2,
    color: FG_2,
    fontWeight: 700,
  },
  prodName: { fontWeight: 600, color: FG_1 },
  prodSub: { fontSize: 12, color: FG_3 },
  badge: {
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 20,
  },
  badgeMapped: { backgroundColor: SUCCESS_BG, color: SUCCESS_TEXT },
  badgeUnmapped: { backgroundColor: BORDER_2, color: FG_3 },
  empty: { textAlign: 'center', color: FG_3, padding: '28px 8px' },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 18,
  },
  pageInfo: { fontSize: 13, color: FG_2, fontVariantNumeric: 'tabular-nums' },
});

InfluenceManufacturerProductsView.propTypes = {
  embedded: PropTypes.bool,
  fixedMapped: PropTypes.oneOf(['mapped', 'unmapped']),
};

InfluenceManufacturerProductsView.defaultProps = {
  embedded: false,
  fixedMapped: null,
};

export default InfluenceManufacturerProductsView;
